/**
 * Lógica pura del avance de obra. Los cálculos (avance ponderado, planificado, estado, curva) los hace el backend:
 * acá sólo se formatea para mostrar, se validan los formularios y se arman los payloads.
 */
import type { BadgeTono, PasoRecorridoVista } from "@/components";
import type { OpcionBuscable } from "./buscar";
import {
  API,
  type Avance,
  type AvanceAdmin,
  type AvanceNovedad,
  type ClaseTarea,
  type CompletadoObra,
  type EstadoTarea,
  type HitoAvance,
  type HitoObra,
  type ItemRecorrido,
  type Novedad,
  type PasoRecorrido,
  type PuntoCurva,
  type RubroAvance,
  type TareaAvance,
} from "./api";
import { formatFecha, hoyISO } from "./format";

export function esAvanceAdmin(a: Avance): a is AvanceAdmin {
  return "tareas" in a;
}

// ------------------------------------------------------------------ estados

export const ETIQUETA_ESTADO_TAREA: Record<EstadoTarea, string> = { sin_empezar: "Sin empezar", en_curso: "En proceso", terminada: "Terminada" };
const TONO_ESTADO_TAREA: Record<EstadoTarea, BadgeTono> = { sin_empezar: "neutro", en_curso: "primario", terminada: "exito" };

export function estadoVisible(t: { estado: EstadoTarea; atrasada?: boolean }): { texto: string; tono: BadgeTono } {
  if (t.atrasada) return { texto: "Atrasada", tono: "peligro" };
  return { texto: ETIQUETA_ESTADO_TAREA[t.estado], tono: TONO_ESTADO_TAREA[t.estado] };
}

export function textoCambio(a: AvanceNovedad): string {
  return `${a.etapa}: ${ETIQUETA_ESTADO_TAREA[a.anterior].toLowerCase()} → ${ETIQUETA_ESTADO_TAREA[a.estado].toLowerCase()}`;
}

// ------------------------------------------------------------------ formatos

export const MENOS = "−"; // signo menos tipográfico (no el guion del teclado)

const UN_DECIMAL = new Intl.NumberFormat("es-AR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

/** "42.5" → "42,5 %" (siempre un decimal: es un valor calculado). */
export function formatPct(valor: string | number): string {
  return `${UN_DECIMAL.format(Number(valor))} %`;
}

/** Diferencia en puntos, con signo: "-5.5" → "−5,5 pts". */
export function formatPuntos(valor: string | number): string {
  const n = Number(valor);
  const signo = n < 0 ? MENOS : n > 0 ? "+" : "";
  return `${signo}${UN_DECIMAL.format(Math.abs(n))} pts`;
}

/** "2026-01-31" → "31 ene 26". Sin pasar por Date, para no correr el día por zona horaria. */
export function formatFechaCorta(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return "";
  const mes = MESES[Number(m[2]) - 1];
  return mes ? `${Number(m[3])} ${mes} ${m[1]!.slice(2)}` : "";
}

// ------------------------------------------------------------------ fechas

const DIA = 86_400_000;

/** Días corridos desde 1970-01-01 (UTC): sirve para restar fechas y como eje del gráfico. */
export function diasDesdeEpoca(iso: string): number {
  const [a = 0, m = 1, d = 1] = iso.split("-").map(Number);
  return Math.round(Date.UTC(a, m - 1, d) / DIA);
}

// ------------------------------------------------------------------ lectores

/** "N de M tareas terminadas" (cronograma) o "N de M hitos cumplidos" (hitos de Configuración). */
export function textoCompletado(c: CompletadoObra, hitos = false): string {
  const base = hitos ? `${c.terminadas} de ${c.total} hitos cumplidos` : `${c.terminadas} de ${c.total} tareas terminadas`;
  return c.en_curso > 0 ? `${base} · ${c.en_curso} en proceso` : base;
}

// ------------------------------------------------------------------ recorrido de la obra

const MARCA_RECORRIDO = { terminada: "hecho", en_curso: "en_curso", sin_empezar: "pendiente" } as const;
const TIPO_NODO = { terreno: "inicio", rubro: "etapa", grupo: "etapa", hito: "hito", fin: "fin" } as const;
const ES_ETAPA = (p: PasoRecorrido) => p.tipo === "terreno" || p.tipo === "rubro" || p.tipo === "grupo";

function subtituloPaso(p: PasoRecorrido): string {
  if (p.estado === "terminada") return p.fecha ? formatFechaCorta(p.fecha) : "Listo";
  if (p.tipo === "grupo" && p.items.length > 0) return `${p.items.filter((i) => i.estado === "terminada").length} de ${p.items.length}`;
  if (p.estado === "en_curso") return p.porcentaje !== null ? `${p.porcentaje} %` : "En curso";
  if (p.tipo === "hito") return "Por cumplir";
  return p.tipo === "terreno" ? "Pendiente" : "Próximamente";
}

/** Lo que dibuja cada nodo de `Recorrido`: las etapas (rubros o hitos de Configuración) van numeradas; firma, hitos y final, no. */
export function nodosRecorrido(pasos: PasoRecorrido[]): PasoRecorridoVista[] {
  let numero = 0;
  return pasos.map((p) => {
    const tipo = TIPO_NODO[p.tipo];
    return {
      clave: p.clave, tipo, marca: MARCA_RECORRIDO[p.estado], titulo: p.nombre, subtitulo: subtituloPaso(p),
      porcentaje: p.porcentaje, numero: tipo === "etapa" ? ++numero : null,
    };
  });
}

/** Dónde está la obra: el primer paso en curso; si no hay, el primero sin terminar; con todo terminado, el último. */
export function pasoActual(pasos: PasoRecorrido[]): string | null {
  const actual = pasos.find((p) => p.estado === "en_curso") ?? pasos.find((p) => p.estado !== "terminada") ?? pasos[pasos.length - 1];
  return actual?.clave ?? null;
}

/** Para la cabecera: "N de M etapas completas" (firma y rubros, o hitos de Configuración) y la etapa actual. */
export function resumenRecorrido(pasos: PasoRecorrido[]): { completas: number; total: number; actual: PasoRecorrido | null } {
  const etapas = pasos.filter(ES_ETAPA);
  const clave = pasoActual(pasos);
  return { completas: etapas.filter((p) => p.estado === "terminada").length, total: etapas.length, actual: pasos.find((p) => p.clave === clave) ?? null };
}

export function estadoPaso(p: PasoRecorrido): { texto: string; tono: BadgeTono } {
  const fecha = p.fecha ? formatFecha(p.fecha) : null;
  if (p.estado === "terminada") {
    const verbo = p.tipo === "terreno" ? "Firmada" : p.tipo === "hito" ? "Se cumplió" : "Terminada";
    return { texto: fecha ? `${verbo} el ${fecha}` : verbo, tono: "exito" };
  }
  if (p.estado === "en_curso") return { texto: fecha ? `En proceso desde el ${fecha}` : "En proceso", tono: "primario" };
  if (p.tipo === "terreno") return { texto: "Pendiente de firma", tono: "neutro" };
  return { texto: p.tipo === "hito" ? "Todavía no se cumplió" : "Próximamente", tono: "neutro" };
}

export function estadoItem(i: ItemRecorrido): { texto: string; tono: BadgeTono } {
  const fecha = i.fecha ? formatFecha(i.fecha) : null;
  if (i.tipo === "subhito") {
    if (i.no_corresponde) return { texto: "No corresponde", tono: "neutro" };
    if (i.estado === "terminada") return { texto: fecha ? `Cumplido el ${fecha}` : "Cumplido", tono: "exito" };
    if (i.automatico) return { texto: "Se tilda sola al firmar la escritura en Terreno", tono: "neutro" };
    return { texto: "Pendiente", tono: "neutro" };
  }
  if (i.estado === "terminada") return { texto: fecha ? `Terminada el ${fecha}` : "Terminada", tono: "exito" };
  if (i.estado === "en_curso") return { texto: fecha ? `En proceso desde el ${fecha}` : "En proceso", tono: "primario" };
  return { texto: "Sin empezar", tono: "neutro" };
}

/** Las tareas (ítems) cuyas novedades se muestran en el detalle del paso: las de un rubro. */
export function etapasDelPaso(p: PasoRecorrido): number[] {
  return p.tipo === "rubro" ? p.items.flatMap((i) => (i.etapa_id === null ? [] : [i.etapa_id])) : [];
}

/** Los sub-hitos de un hito de Configuración: sus novedades son las que los completaron. */
export function hitosDelPaso(p: PasoRecorrido): number[] {
  return p.tipo === "grupo" ? p.items.flatMap((i) => (i.hito_id == null ? [] : [i.hito_id])) : [];
}

/** El filtro de `GET /avance/novedades` para el detalle de un paso; null si el paso no tiene novedades propias. */
export function filtroNovedadesPaso(p: PasoRecorrido): string | null {
  const filtros = [...etapasDelPaso(p).map((id) => `etapa_id=${id}`), ...hitosDelPaso(p).map((id) => `hito_id=${id}`)];
  return filtros.length > 0 ? filtros.join("&") : null;
}

export function textoTareasPaso(p: PasoRecorrido): string {
  return textoCompletado(
    {
      porcentaje: p.porcentaje ?? 0, total: p.items.length,
      terminadas: p.items.filter((i) => i.estado === "terminada").length, en_curso: p.items.filter((i) => i.estado === "en_curso").length,
    },
    p.tipo === "grupo",
  );
}

// ------------------------------------------------------------------ control interno

export interface GrupoRubro {
  rubro: RubroAvance;
  tareas: TareaAvance[];
  hitos: HitoAvance[];
  abierta: boolean; // hay tareas en proceso
}

/** Los rubros del cronograma (por cuándo arrancan) con sus tareas e hitos. */
export function rubrosAgrupados(a: AvanceAdmin): GrupoRubro[] {
  return a.rubros.map((rubro) => {
    const tareas = a.tareas.filter((t) => t.rubro_id === rubro.id);
    return { rubro, tareas, hitos: a.hitos.filter((h) => h.rubro_id === rubro.id), abierta: tareas.some((t) => t.estado === "en_curso") };
  });
}

export interface BarraTarea {
  id: number;
  nombre: string;
  detalle: string;
  izquierda: number;
  ancho: number;
  avance: number;
  tono: BadgeTono;
  estado: string;
}

const AVANCE_ESTADO: Record<EstadoTarea, number> = { sin_empezar: 0, en_curso: 50, terminada: 100 };
const redondear2 = (n: number) => Math.round(n * 100) / 100;

/** Barras del `Cronograma` para las tareas de un rubro, sobre el largo de toda la obra. Sin fechas, lista vacía. */
export function barrasTareas(tareas: TareaAvance[], desde: string | null, hasta: string | null, hoy: string = hoyISO()): { etapas: BarraTarea[]; hoy: number | null } {
  if (!desde || !hasta || tareas.some((t) => !t.inicio_plan || !t.fin_plan)) return { etapas: [], hoy: null };
  const origen = diasDesdeEpoca(desde);
  const total = diasDesdeEpoca(hasta) - origen + 1;
  const pct = (dias: number) => redondear2((dias / total) * 100);
  const diaHoy = diasDesdeEpoca(hoy) - origen + 1;
  return {
    hoy: diaHoy >= 1 && diaHoy <= total ? pct(diaHoy) : null,
    etapas: tareas.map((t) => {
      const visible = estadoVisible(t);
      return {
        id: t.id,
        nombre: t.nombre,
        detalle: `${formatFecha(t.inicio_plan!)} al ${formatFecha(t.fin_plan!)} · ${t.duracion} días hábiles`,
        izquierda: pct(diasDesdeEpoca(t.inicio_plan!) - origen),
        ancho: pct(diasDesdeEpoca(t.fin_plan!) - diasDesdeEpoca(t.inicio_plan!) + 1),
        avance: AVANCE_ESTADO[t.estado],
        tono: visible.tono,
        estado: visible.texto,
      };
    }),
  };
}

/** "2027-03-06" es sábado. Sin pasar por la zona horaria local. */
export function esDiaHabil(iso: string): boolean {
  const [a, m, d] = iso.split("-").map(Number);
  const dia = new Date(Date.UTC(a!, m! - 1, d!)).getUTCDay();
  return dia !== 0 && dia !== 6;
}

export function validarInicio(iso: string): string | null {
  if (!iso) return null;
  return esDiaHabil(iso) ? null : "Tiene que ser un día hábil (de lunes a viernes)";
}

// ------------------------------------------------------------------ formulario de la novedad

export interface CambioEstado {
  etapa_id: string;
  estado: EstadoTarea | "";
}

export interface FormNovedad {
  fecha: string;
  titulo: string;
  texto: string;
  cambios: CambioEstado[];
  /** Sólo se usa (y se manda) en el alta: la edición no vuelve a avisar. */
  avisar: boolean;
}

export function formNovedad(n?: Novedad, hoy: string = hoyISO()): FormNovedad {
  if (!n) return { fecha: hoy, titulo: "", texto: "", cambios: [], avisar: true };
  return { fecha: n.fecha, titulo: n.titulo, texto: n.texto ?? "", cambios: n.avances.map((a) => ({ etapa_id: String(a.etapa_id), estado: a.estado })), avisar: false };
}

export interface ErroresNovedad {
  fecha?: string;
  titulo?: string;
  texto?: string;
  estados: Record<string, string>;
}

export function validarNovedad(f: FormNovedad, hoy: string = hoyISO()): ErroresNovedad {
  const e: ErroresNovedad = { estados: {} };
  if (!f.fecha) e.fecha = "Ingresá la fecha";
  else if (f.fecha > hoy) e.fecha = "La fecha no puede ser futura";
  const titulo = f.titulo.trim();
  if (!titulo) e.titulo = "Ingresá el título";
  else if (titulo.length > 120) e.titulo = "Hasta 120 caracteres";
  if (f.texto.trim().length > 4000) e.texto = "Hasta 4000 caracteres";
  for (const c of f.cambios) if (!c.estado) e.estados[c.etapa_id] = "Elegí el estado";
  return e;
}

export function hayErroresNovedad(e: ErroresNovedad): boolean {
  return Boolean(e.fecha || e.titulo || e.texto) || Object.keys(e.estados).length > 0;
}

export function aPayloadNovedad(f: FormNovedad) {
  return {
    fecha: f.fecha,
    titulo: f.titulo.trim(),
    texto: f.texto.trim() || null,
    avances: f.cambios.map((c) => ({ etapa_id: Number(c.etapa_id), estado: c.estado as EstadoTarea })),
  };
}

/** Cuerpo del alta: el PATCH no lleva `avisar` (los avisos salen sólo al publicar). */
export function aPayloadNovedadNueva(f: FormNovedad) {
  return { ...aPayloadNovedad(f), avisar: f.avisar };
}

/** `POST /avance/hitos/{id}/completar`: el título lo arma el backend. */
export function aPayloadCumplido(f: FormNovedad) {
  return { fecha: f.fecha, texto: f.texto.trim() || null, avisar: f.avisar };
}

// ------------------------------------------------------------------ hitos de Configuración

export function validarNombreHito(nombre: string): string | null {
  const n = nombre.trim();
  if (!n) return "Ingresá el nombre";
  return n.length > 120 ? "Hasta 120 caracteres" : null;
}

export function validarDescripcionHito(descripcion: string): string | null {
  return descripcion.trim().length > 300 ? "Hasta 300 caracteres" : null;
}

/** "2 de 3 cumplidos" / "Sin sub-hitos". */
export function textoSubHitos(h: HitoObra): string {
  if (h.sub.length === 0) return "Sin sub-hitos";
  return `${h.sub.filter((s) => s.completado_el !== null).length} de ${h.sub.length} cumplidos`;
}

/** Tareas que todavía no están en la novedad, para el buscador "Agregar tarea": "Rubro › ítem". */
export function opcionesEtapas(a: AvanceAdmin, elegidas: string[]): OpcionBuscable[] {
  const rubros = new Map(a.rubros.map((r) => [r.id, r.nombre]));
  return a.tareas
    .map((t) => ({ valor: String(t.id), etiqueta: `${rubros.get(t.rubro_id) ?? ""} › ${t.nombre}` }))
    .filter((o) => !elegidas.includes(o.valor));
}

/**
 * Estado "de hoy" de una etapa para mostrar en el formulario de novedad. Al editar una novedad existente, el estado
 * en `admin` ya incluye el cambio de esa misma novedad: para sus propias tareas hay que mostrar `anterior`, el
 * estado previo a ella, no el actual.
 */
export function estadoActual(a: AvanceAdmin, etapaId: string, novedad?: Novedad | null): EstadoTarea {
  const id = Number(etapaId);
  const previo = novedad?.avances.find((av) => av.etapa_id === id)?.anterior;
  if (previo !== undefined) return previo;
  return a.tareas.find((t) => t.id === id)?.estado ?? "sin_empezar";
}

export function nombreEtapa(a: AvanceAdmin, etapaId: string): string {
  return opcionesEtapas(a, []).find((o) => o.valor === etapaId)?.etiqueta ?? "";
}

// ------------------------------------------------------------------ gráficos

export interface PuntoGrafico {
  t: number; // días desde 1970: el eje del tiempo
  fecha: string;
  planificado: number;
  real: number | null;
}

export function puntosGrafico(curva: PuntoCurva[]): PuntoGrafico[] {
  return curva.map((p) => ({ t: diasDesdeEpoca(p.fecha), fecha: p.fecha, planificado: Number(p.planificado), real: p.real === null ? null : Number(p.real) }));
}

// ------------------------------------------------------------------ Inicio

/** El Inicio muestra la tarjeta de avance recién con tareas en el cronograma (o hitos de Configuración). */
export function hayAvance(a: Avance): boolean {
  return a.completado.total > 0;
}

export function tarjetaAvance(a: Avance): { valor: string; detalle: string; porcentaje: number | null } {
  const c = a.completado;
  if (c.total === 0) return { valor: "—", detalle: esAvanceAdmin(a) ? "Sin hitos cargados" : "Todavía no hay avance publicado", porcentaje: null };
  const base = { valor: `${c.porcentaje} %`, porcentaje: c.porcentaje };
  if (!esAvanceAdmin(a) || a.fuente === "hitos") {
    const actual = resumenRecorrido(a.recorrido).actual;
    return { ...base, detalle: actual ? `Ahora: ${actual.nombre}` : textoCompletado(c, a.fuente === "hitos") };
  }
  if (!a.resumen) return { ...base, detalle: textoCompletado(c) };
  if (a.resumen.diferencia === null) return { ...base, detalle: `${textoCompletado(c)} · plan a definir` };
  const diferencia = Number(a.resumen.diferencia);
  const puntos = `${UN_DECIMAL.format(Math.abs(diferencia))} pts`;
  return { ...base, detalle: diferencia === 0 ? "En línea con el plan" : diferencia < 0 ? `${puntos} por debajo del plan` : `${puntos} por encima del plan` };
}

// ------------------------------------------------------------------ fotos

export type VersionFoto = "pantalla" | "miniatura";

/** URL absoluta de una foto: va en `<img src>` (mismo origen, la cookie viaja sola). */
export function rutaFoto(fid: number, fotoId: number, version: VersionFoto): string {
  return `${API}/api/fideicomisos/${fid}/avance/fotos/${fotoId}?version=${version}`;
}

export interface FotoDeGaleria {
  id: number;
  miniatura: string;
  pantalla: string;
  alt: string;
  ancho: number;
  alto: number;
}

export function fotosGaleria(fid: number, n: Pick<Novedad, "titulo" | "fotos">): FotoDeGaleria[] {
  return n.fotos.map((f, i) => ({
    id: f.id,
    miniatura: rutaFoto(fid, f.id, "miniatura"),
    pantalla: rutaFoto(fid, f.id, "pantalla"),
    alt: f.epigrafe ?? `${n.titulo}, foto ${i + 1}`,
    ancho: f.ancho,
    alto: f.alto,
  }));
}

// ------------------------------------------------------------------ fotos para subir

/** Mismos topes que el backend (app/avance/reglas.py y app/adjuntos/reglas.py). */
export const MAX_FOTOS = 10;
const EXTENSIONES_FOTO = ["jpg", "jpeg", "png", "webp", "heic"];
export const ACCEPT_FOTO = ".jpg,.jpeg,.png,.webp,.heic,image/*";
const MAX_BYTES_FOTO = 10 * 1024 * 1024;

export function validarFoto(archivo: File): string | null {
  const extension = archivo.name.includes(".") ? (archivo.name.split(".").pop()?.toLowerCase() ?? "") : "";
  if (!EXTENSIONES_FOTO.includes(extension)) return "Sólo se aceptan imágenes (JPG, PNG, WEBP, HEIC)";
  if (archivo.size > MAX_BYTES_FOTO) return `${archivo.name} supera los 10 MB`;
  return null;
}

/** Tipos de tarea del recorrido del desarrollo (socio de Mati, 2026-11-03), con su color. */
export const CLASE_TAREA: Record<ClaseTarea, { texto: string; tono: BadgeTono }> = {
  hito: { texto: "Hito / permiso", tono: "primario" },
  tramite: { texto: "Trámite / inspección", tono: "alerta" },
  pago: { texto: "Pago", tono: "exito" },
  obra: { texto: "Obra", tono: "neutro" },
};

/** Al marcar cumplida una tarea, de entrada sólo avisan los hitos (y los sub-hitos sin tipo, como antes). */
export const avisaDeEntrada = (i: Pick<ItemRecorrido, "clase">): boolean => !i.clase || i.clase === "hito";
